import React, { useEffect, useState } from 'react';
import {
    CheckCircle,
    XCircle,
    AlertCircle,
    Info,
    X,
    Bell,
    AlertTriangle
} from 'lucide-react';

const NotificationToast = ({ notifications = [], removeNotification }) => {
    return (
        <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm w-full">
            {notifications.map((notification) => (
                <Toast
                    key={notification.id}
                    notification={notification}
                    onRemove={removeNotification}
                />
            ))}
        </div>
    );
};

const Toast = ({ notification, onRemove }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);

    useEffect(() => {
        // Trigger enter animation
        setTimeout(() => setIsVisible(true), 10);
    }, []);

    const handleRemove = () => {
        setIsLeaving(true);
        setTimeout(() => onRemove(notification.id), 300);
    };

    const getToastConfig = (type) => {
        const configs = {
            success: {
                icon: CheckCircle,
                bgColor: 'bg-highland-50',
                borderColor: 'border-highland-100',
                iconColor: 'text-highland-500',
                titleColor: 'text-highland-700',
                textColor: 'text-highland-600',
                progressColor: 'bg-highland-500'
            },
            error: {
                icon: XCircle,
                bgColor: 'bg-murram-100/40',
                borderColor: 'border-murram-100',
                iconColor: 'text-murram-500',
                titleColor: 'text-murram-600',
                textColor: 'text-murram-600',
                progressColor: 'bg-murram-500'
            },
            warning: {
                icon: AlertTriangle,
                bgColor: 'bg-dawn-100/40',
                borderColor: 'border-dawn-100',
                iconColor: 'text-dawn-600',
                titleColor: 'text-dawn-600',
                textColor: 'text-dawn-600',
                progressColor: 'bg-dawn-500'
            },
            info: {
                icon: Info,
                bgColor: 'bg-jacaranda-100',
                borderColor: 'border-jacaranda-100',
                iconColor: 'text-jacaranda-500',
                titleColor: 'text-jacaranda-600',
                textColor: 'text-jacaranda-600',
                progressColor: 'bg-jacaranda-500'
            }
        };
        return configs[type] || configs.info;
    };

    const config = getToastConfig(notification.type);
    const Icon = config.icon;

    const getTitle = (type) => {
        const titles = {
            success: 'Success',
            error: 'Error',
            warning: 'Warning',
            info: 'Info'
        };
        return notification.title || titles[type] || 'Notification';
    };

    return (
        <div
            className={`transform transition-all duration-300 ease-in-out ${
                isVisible && !isLeaving
                    ? 'translate-x-0 opacity-100'
                    : 'translate-x-full opacity-0'
            }`}
        >
            <div
                className={`${config.bgColor} ${config.borderColor} border rounded-lg shadow-lg p-4 mb-2 max-w-sm relative overflow-hidden`}
            >
                {/* Progress bar (optional) */}
                {notification.showProgress && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-soil-100">
                        <div
                            className={`h-full ${config.progressColor} transition-all duration-100 ease-linear`}
                            style={{ width: `${notification.progress || 0}%` }}
                        />
                    </div>
                )}

                <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                        <Icon className={`w-5 h-5 ${config.iconColor}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${config.titleColor}`}>
                            {getTitle(notification.type)}
                        </div>
                        <div className={`text-sm ${config.textColor} mt-1`}>
                            {notification.message}
                        </div>

                        {/* Additional content */}
                        {notification.description && (
                            <div className={`text-xs ${config.textColor} opacity-75 mt-2`}>
                                {notification.description}
                            </div>
                        )}

                        {/* Action buttons */}
                        {notification.actions && (
                            <div className="flex space-x-2 mt-3">
                                {notification.actions.map((action, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            action.onClick();
                                            handleRemove();
                                        }}
                                        className={`text-xs px-3 py-1 rounded font-medium transition-colors duration-200 ${
                                            action.primary
                                                ? `${config.progressColor.replace('bg-', 'bg-')} text-white hover:opacity-90`
                                                : `${config.textColor} hover:${config.bgColor.replace('50', '100')}`
                                        }`}
                                    >
                                        {action.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Close button */}
                    <button
                        onClick={handleRemove}
                        className={`flex-shrink-0 ${config.textColor} hover:${config.titleColor} transition-colors duration-200`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Enhanced notification hook for easy usage
export const useToast = () => {
    const [notifications, setNotifications] = useState([]);

    const addNotification = (message, type = 'info', options = {}) => {
        const notification = {
            id: Date.now() + Math.random(),
            message,
            type,
            timestamp: new Date(),
            ...options
        };

        setNotifications(prev => [...prev, notification]);

        // Auto remove notification
        const duration = options.duration || 5000;
        if (duration > 0) {
            setTimeout(() => {
                removeNotification(notification.id);
            }, duration);
        }

        return notification.id;
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
    };

    const clearAll = () => {
        setNotifications([]);
    };

    // Convenience methods
    const success = (message, options = {}) => addNotification(message, 'success', options);
    const error = (message, options = {}) => addNotification(message, 'error', options);
    const warning = (message, options = {}) => addNotification(message, 'warning', options);
    const info = (message, options = {}) => addNotification(message, 'info', options);

    return {
        notifications,
        addNotification,
        removeNotification,
        clearAll,
        success,
        error,
        warning,
        info
    };
};

// Global notification container component
export const NotificationContainer = () => {
    const [notifications, setNotifications] = useState([]);

    // Global notification function that can be called from anywhere
    window.showNotification = (message, type = 'info', options = {}) => {
        const notification = {
            id: Date.now() + Math.random(),
            message,
            type,
            timestamp: new Date(),
            ...options
        };

        setNotifications(prev => [...prev, notification]);

        const duration = options.duration || 5000;
        if (duration > 0) {
            setTimeout(() => {
                setNotifications(prev => prev.filter(notif => notif.id !== notification.id));
            }, duration);
        }
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
    };

    return <NotificationToast notifications={notifications} removeNotification={removeNotification} />;
};

// Different positioning options
export const BottomToast = ({ notifications, removeNotification }) => {
    return (
        <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm w-full">
            {notifications.map((notification) => (
                <Toast
                    key={notification.id}
                    notification={notification}
                    onRemove={removeNotification}
                />
            ))}
        </div>
    );
};

export const TopCenterToast = ({ notifications, removeNotification }) => {
    return (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 space-y-2 max-w-sm w-full">
            {notifications.map((notification) => (
                <Toast
                    key={notification.id}
                    notification={notification}
                    onRemove={removeNotification}
                />
            ))}
        </div>
    );
};

// Compact toast variant
export const CompactToast = ({ notification, onRemove }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setTimeout(() => setIsVisible(true), 10);
    }, []);

    const config = {
        success: { color: 'bg-highland-500', icon: CheckCircle },
        error: { color: 'bg-murram-500', icon: XCircle },
        warning: { color: 'bg-dawn-500', icon: AlertCircle },
        info: { color: 'bg-jacaranda-500', icon: Info }
    };

    const { color, icon: Icon } = config[notification.type] || config.info;

    return (
        <div
            className={`transform transition-all duration-300 ${
                isVisible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
            }`}
        >
            <div className={`${color} text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 text-sm`}>
                <Icon className="w-4 h-4" />
                <span className="flex-1">{notification.message}</span>
                <button onClick={() => onRemove(notification.id)}>
                    <X className="w-4 h-4 hover:bg-white hover:bg-opacity-20 rounded" />
                </button>
            </div>
        </div>
    );
};

export default NotificationToast;